import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BRAND, waLink } from "@/data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/membership", label: "Membership" },
  { to: "/contact", label: "Contact Us" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-sand/85 backdrop-blur-xl border-bronze/10 shadow-[0_8px_30px_rgba(31,29,26,0.06)]"
          : "bg-sand/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[0.8rem] uppercase tracking-[0.18em] transition-colors duration-300 hover:text-bronze ${
                  isActive ? "text-bronze" : "text-ink/70"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-[width] duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={BRAND.tel}
            data-testid="header-call-btn"
            className="inline-flex items-center gap-2 rounded-full border border-bronze/25 px-5 py-2.5 text-[0.75rem] uppercase tracking-[0.15em] text-bronze transition-colors duration-300 hover:bg-bronze hover:text-cream"
          >
            <Phone size={14} /> Call
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="inline-flex items-center gap-2 rounded-full bg-wa px-5 py-2.5 text-[0.75rem] uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-wa-dark"
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-ink p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-sand/95 backdrop-blur-xl border-t border-bronze/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `font-serif text-2xl ${isActive ? "text-bronze" : "text-ink"}`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="flex gap-3 pt-4">
                <a
                  href={BRAND.tel}
                  data-testid="mobile-call-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-bronze/30 px-4 py-3 text-xs uppercase tracking-[0.15em] text-bronze"
                >
                  <Phone size={14} /> Call Now
                </a>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-wa px-4 py-3 text-xs uppercase tracking-[0.15em] text-white"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
